import React, { Fragment, useMemo, useState } from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import * as yup from 'yup';
import { useForm, useFormState } from 'react-hook-form';
import { always, cond, equals, pick } from 'ramda';
import NextLink from 'next/link';
import useLogin from '@bigcommerce/storefront-data-hooks/use-login';
import { useRouter } from 'next/router';
import { StatusCodes } from 'http-status-codes';
import { useAsync } from 'react-use';

import { useYupValidationResolver } from '@hooks/useYup';
import reportError from '@utils/reportError';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import {
  Button,
  BUTTON_SIZE,
  ButtonRightContainer,
  CheckboxField,
  Error,
  formMessages,
  Link,
  PASSWORD_REGEX,
  PHONE_REGEX,
  TextField,
} from '@components/form';
import { CenterContainer } from '@components/centerContainer';
import { USER_WAITLISTED_ITEMS } from '@components/waitlistButton/waitlistButton.constants';
import { LineTitle, TextMessage } from '@components/common';
import { formatIntlLink } from '@utils/helpers';
import { ls } from '@utils/helpers';
import {
  CUSTOMER_FORM_EMAIL,
  CUSTOMER_FORM_FIRST_NAME,
  CUSTOMER_FORM_LAST_NAME,
  CUSTOMER_FORM_TERMS_AND_NEWSLETTER,
  CUSTOMER_FORM_PASSWORD,
  CUSTOMER_FORM_PHONE,
  CUSTOMER_FORM_TERMS_ONLY,
  CUSTOMER_FORM_TERMS,
  CUSTOMER_FORM_SUBSCRIBE_NEWSLETTER,
  CUSTOMER_FORM_TERMS_ACCEPTED,
} from '@modules/customer';
import { RegisterFormTypes } from '@definitions/customer.types';
import { useCustomer, useRegisterCustomer } from '@hooks/useCustomer';
import { ROUTE } from '@utils/constants';
import api from '@services/nextAPI';
import pintrk, { PINTEREST_EVENTS } from '@utils/pinterestTag';
import { useTrackConversions } from '@hooks/useTrackConversions';
import { CONVERSIONS_EVENTS, CONVERSIONS_STATUS } from '@definitions/conversions.types';

export enum ERROR_TYPE {
  GENERAL = 'general',
  CREATE = 'create',
}

export const RegisterForm = () => {
  const intl = useIntl();
  const { registerCustomer } = useRegisterCustomer();
  const { isAuthed, loadingCustomer } = useCustomer();
  const { sendConversion } = useTrackConversions();
  const router = useRouter();
  const login = useLogin();
  const [error, setError] = useState<ERROR_TYPE | null>(null);
  const [loading, setLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const validationSchema = useMemo(
    () =>
      yup.object({
        [CUSTOMER_FORM_FIRST_NAME]: yup.string().required(intl.formatMessage(formMessages.required)),
        [CUSTOMER_FORM_LAST_NAME]: yup.string().required(intl.formatMessage(formMessages.required)),
        [CUSTOMER_FORM_PHONE]: yup
          .string()
          .matches(PHONE_REGEX, { message: intl.formatMessage(formMessages.phoneError), excludeEmptyString: true }),
        [CUSTOMER_FORM_EMAIL]: yup
          .string()
          .email(intl.formatMessage(formMessages.emailError))
          .required(intl.formatMessage(formMessages.required)),
        [CUSTOMER_FORM_PASSWORD]: yup
          .string()
          .matches(PASSWORD_REGEX, intl.formatMessage(formMessages.passwordError))
          .required(intl.formatMessage(formMessages.required)),
        [CUSTOMER_FORM_EMAIL]: yup
          .string()
          .email(intl.formatMessage(formMessages.emailError))
          .required(intl.formatMessage(formMessages.required)),
        [CUSTOMER_FORM_TERMS]: yup
          .string()
          .oneOf(
            [CUSTOMER_FORM_TERMS_ONLY, CUSTOMER_FORM_TERMS_AND_NEWSLETTER],
            intl.formatMessage(formMessages.termsRequired)
          )
          .required(intl.formatMessage(formMessages.termsRequired)),
      }),
    []
  );
  const resolver = useYupValidationResolver(validationSchema);
  const { register, handleSubmit, control } = useForm<RegisterFormTypes>({
    defaultValues: {
      [CUSTOMER_FORM_FIRST_NAME]: '',
      [CUSTOMER_FORM_LAST_NAME]: '',
      [CUSTOMER_FORM_PHONE]: '',
      [CUSTOMER_FORM_EMAIL]: '',
      [CUSTOMER_FORM_PASSWORD]: '',
      [CUSTOMER_FORM_TERMS]: '',
    },
    resolver,
  });
  const { errors } = useFormState({ control });

  const registerUser = async (formData: RegisterFormTypes) => {
    try {
      setLoading(true);
      setError(null);
      const data = {
        ...pick(
          [
            CUSTOMER_FORM_FIRST_NAME,
            CUSTOMER_FORM_LAST_NAME,
            CUSTOMER_FORM_PHONE,
            CUSTOMER_FORM_EMAIL,
            CUSTOMER_FORM_PASSWORD,
          ],
          formData
        ),
        [CUSTOMER_FORM_SUBSCRIBE_NEWSLETTER]: formData[CUSTOMER_FORM_TERMS] === CUSTOMER_FORM_TERMS_AND_NEWSLETTER,
        [CUSTOMER_FORM_TERMS_ACCEPTED]: true,
      };
      await registerCustomer(data);
      await login({ email: formData[CUSTOMER_FORM_EMAIL], password: formData[CUSTOMER_FORM_PASSWORD] });
      setCreateSuccess(true);
      ls.remove(USER_WAITLISTED_ITEMS);
      if (formData[CUSTOMER_FORM_TERMS] === CUSTOMER_FORM_TERMS_AND_NEWSLETTER) {
        await api.post('email/subscribe', { email: formData[CUSTOMER_FORM_EMAIL], tags: ['Account Creation'] });
        await sendConversion({
          eventType: CONVERSIONS_EVENTS.COMPLETE_REGISTRATION,
          status: CONVERSIONS_STATUS.NEWSLETTER,
        });
        pintrk.event(PINTEREST_EVENTS.SIGNUP, { lead_type: 'Newsletter' });
      }
      await sendConversion({
        eventType: CONVERSIONS_EVENTS.COMPLETE_REGISTRATION,
        status: CONVERSIONS_STATUS.ACCOUNT_CREATED,
      });
    } catch (e) {
      reportError(e);
      const error = e as { status: StatusCodes };
      if (error.status === StatusCodes.UNPROCESSABLE_ENTITY) {
        return setError(ERROR_TYPE.CREATE);
      }

      return setError(ERROR_TYPE.GENERAL);
    } finally {
      setLoading(false);
    }
  };

  useAsync(async () => {
    if (isAuthed && !loadingCustomer) {
      await router.push('/account');
    }
  }, [loadingCustomer]);

  const getErrorMessage = cond([
    [equals(ERROR_TYPE.GENERAL), always(formMessages.generalError)],
    [equals(ERROR_TYPE.CREATE), always(formMessages.createUserError)],
  ]) as (error: ERROR_TYPE | null) => MessageDescriptor;

  const renderError = renderWhenTrue(
    always(
      <Error>
        <FormattedMessage {...getErrorMessage(error)} />
      </Error>
    )
  );

  const renderForm = renderWhenTrueOtherwise(
    always(
      <Fragment>
        <LineTitle>
          <FormattedMessage id="registerForm.thank" defaultMessage="Welcome!:" />
        </LineTitle>
        <TextMessage>
          <FormattedMessage
            id="registerForm.successMessage"
            defaultMessage="Thank you for registering with Platform. You’ll receive a confirmation email with your account details shortly, but please feel free to begin using your account in the meantime."
          />
        </TextMessage>
        <ButtonRightContainer>
          <NextLink href="/account" passHref>
            <Link>
              <FormattedMessage id="registerForm.redirect" defaultMessage="See account" />
            </Link>
          </NextLink>
        </ButtonRightContainer>
      </Fragment>
    ),
    always(
      <form onSubmit={handleSubmit(registerUser)}>
        <LineTitle>
          <FormattedMessage id="registerForm.title" defaultMessage="Create an account:" />
        </LineTitle>
        <TextField
          register={register(CUSTOMER_FORM_FIRST_NAME)}
          error={errors[CUSTOMER_FORM_FIRST_NAME]}
          placeholder={intl.formatMessage(formMessages.firstName)}
        />
        <TextField
          register={register(CUSTOMER_FORM_LAST_NAME)}
          error={errors[CUSTOMER_FORM_LAST_NAME]}
          placeholder={intl.formatMessage(formMessages.lastName)}
        />
        <TextField
          register={register(CUSTOMER_FORM_PHONE)}
          error={errors[CUSTOMER_FORM_PHONE]}
          placeholder={intl.formatMessage(formMessages.phoneNumber)}
          optional
        />
        <TextField
          register={register(CUSTOMER_FORM_EMAIL)}
          error={errors[CUSTOMER_FORM_EMAIL]}
          placeholder={intl.formatMessage(formMessages.email)}
        />
        <TextField
          type="password"
          register={register(CUSTOMER_FORM_PASSWORD)}
          error={errors[CUSTOMER_FORM_PASSWORD]}
          placeholder={intl.formatMessage(formMessages.password)}
        />
        <CheckboxField
          register={register(CUSTOMER_FORM_TERMS)}
          value={CUSTOMER_FORM_TERMS_AND_NEWSLETTER}
          type="radio"
          id={CUSTOMER_FORM_TERMS_AND_NEWSLETTER}
          error={errors[CUSTOMER_FORM_TERMS]}
          label={intl.formatMessage(formMessages.termsAndNewsletter, {
            terms: formatIntlLink(ROUTE.TERMS_AND_CONDITIONS),
          })}
        />
        <CheckboxField
          register={register(CUSTOMER_FORM_TERMS)}
          value={CUSTOMER_FORM_TERMS_ONLY}
          id={CUSTOMER_FORM_TERMS_ONLY}
          type="radio"
          error={errors[CUSTOMER_FORM_TERMS]}
          label={intl.formatMessage(formMessages.termsOnly, {
            terms: formatIntlLink(ROUTE.TERMS_AND_CONDITIONS),
          })}
        />
        {renderError(!!error)}
        <ButtonRightContainer>
          <Button type="submit" disabled={loading} inverted size={BUTTON_SIZE.L} wide>
            <FormattedMessage id="registerForm.request" defaultMessage="Register" />
          </Button>
        </ButtonRightContainer>
      </form>
    )
  );

  return <CenterContainer>{renderForm(createSuccess)}</CenterContainer>;
};
